import React, { Fragment, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import HeaderV2 from "../../components/headerV2/HeaderV2";
import FooterV2 from "../../components/footerV2/FooterV2";
import AboutHero from "../../components/aboutHero/AboutHero";
import useScrollReveal from "../../hooks/useScrollReveal";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import "../AboutUsPage/aboutV2.css";

/* Photo supplied in the Sep 2026 website review (lives in /public). */
const TestimonialsImg = process.env.PUBLIC_URL + "/images/about-v2/testimonials.jpg";

const TESTIMONIALS = [
  {
    quote:
      "Life Saver Army gave me the chance to give something that no amount of money can buy. I registered as a donor six months ago, and last week, I received a call. I donated, and a few days later, I heard that the patient had recovered. That moment changed how I think about what one person can do.",
    who: "Blood Donor, Delhi",
  },
  {
    quote:
      "When we needed blood urgently for my mother’s surgery, the hospital told us they’d run out of her blood group. A nurse pointed us to a Life Saver Army QR code on the wall. Within two hours, a volunteer coordinator had found a match. That moment is why I trust Life Saver Army. They helped save her life.",
    who: "Family Member, Ram Krishna Mission Hospital, Varanasi",
  },
  {
    quote:
      "Volunteering with Life Saver Army as a field coordinator has been the most fulfilling thing I’ve ever done. I’ve helped register donors, organise camps, and been present when someone in crisis found a match. Seeing lives protected through a network built together — that’s why I do this every day.",
    who: "Volunteer",
  },
  {
    quote:
      "I never thought donating blood would feel so personal. After I donated through Life Saver Army, the team sent me a message to say my donation had helped a patient in surgery. That message stayed with me. I’ve donated three times since, and I’ve brought five friends into the network too.",
    who: "Repeat Blood Donor, Maharashtra",
  },
  {
    quote:
      "Our college had never hosted a proper blood donation camp before. Life Saver Army's team handled everything; logistics, donor registration, coordination with the blood bank; and we collected 85 units in a single day. The students were inspired, and three of them have now joined as Life Saver Army volunteers.",
    who: "NSS Unit Head, MGKVP University, Varanasi",
  },
  {
    quote:
      "When I registered with Life Saver Army, I was not sure I was brave enough to donate. The team made it so simple and supportive that any fear I had disappeared. I donated for the first time last month. I felt proud walking out of that hospital. I am now telling everyone I know to register.",
    who: "First-Time Blood Donor, Goa",
  },
  {
    quote:
      "Partnering with Life Saver Army has helped our blood bank reduce its shortfall during critical periods. Their donor network is responsive, their coordinators are professional, and the QR system means we are never starting from scratch in an emergency. This is exactly the kind of community partnership that saves lives.",
    who: "Blood Bank Officer, Partner Hospital, Rajasthan",
  },
];

const Testimonials = ({ path }) => {
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Testimonials" });
  const [schemadata, setSchemadata] = useState({});

  const rootRef = useRef(null);
  useScrollReveal(rootRef);

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];

    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      tempMetadata = {
        title: "JJFIndia | Testimonials",
        description: "JJFIndia Impact and Stories page",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/impact/testimonials",
        openGraph: {
          title: "JJFIndia | Testimonials",
          description: "JJFIndia Impact and Stories page",
          url: "https://jjfindia.com/impact/testimonials",
        },
        twitter: {
          card: "summary_large_image",
          title: "JJFIndia | Testimonials",
          description: "JJFIndia Impact and Stories page",
        },
      };
      setMetadata(tempMetadata);
    }

    if (tempSchemadata) {
      setSchemadata(tempSchemadata);
    }
  }, [path]);

  const scrollTop = () => window.scrollTo(10, 0);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <div className="jjfv2 about-live" ref={rootRef}>
        <HeaderV2 />

        <main className="page_content">
          {/* ---------- Page hero ---------- */}
          <AboutHero
            eyebrow="Voices of gratitude"
            title="Testimonials"
            crumbs={[
              { label: "Home", to: "/" },
              { label: "Impacts & Stories", to: "/impact/success-stories" },
              { label: "Testimonials" },
            ]}
          />

          {/* ---------- Intro ---------- */}
          <section className="section about" id="testimonials-intro" style={{ paddingTop: "36px", paddingBottom: "36px" }}>
            <div className="wrap split" style={{ position: "relative", zIndex: 1 }}>
              <div className="about-media reveal">
                <span className="leaf-frame" aria-hidden="true"></span>
                <div className="ph">
                  <img src={TestimonialsImg} alt="A Life Saver Army volunteer sharing her experience" loading="lazy" />
                </div>
              </div>
              <div className="about-copy reveal d1">
                <span className="eyebrow">From donors, volunteers, patients and partners</span>
                <h2>In Their Own Words</h2>
                <p className="lead">
                  Donors describe their first camp. Volunteers describe the calls they answer.
                  Hospital partners describe what changes when a donor network is a phone call away.
                </p>
              </div>
            </div>
          </section>

          {/* ---------- Testimonials ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">In their own words</span>
                <h2>What People Say</h2>
              </div>
              <div className="testimonials">
                {TESTIMONIALS.map((t, i) => (
                  <article className={"testimonial reveal" + (i % 2 ? " d1" : "")} key={i}>
                    <span className="testimonial__mark" aria-hidden="true">&ldquo;</span>
                    <p>{t.quote}</p>
                    <cite>&mdash; {t.who}</cite>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- Join the change ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Join the Change</span>
                <h2>Be a Part of the Impact</h2>
                <p className="lead">
                  These stories are a glimpse of the impact Life Saver Army, an initiative of Jainam
                  Jivika Foundation, is creating across India. Every testimonial reflects the power of
                  what happens when communities come together to protect one another. Register as a
                  donor, volunteer your time, or partner with us; and add your story to theirs.
                </p>
                <Link to="/join-us/volunteer-opportunities" onClick={scrollTop} className="btn btn--green" style={{ marginTop: "1.7rem" }}>
                  Get Involved
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <FooterV2 />
      </div>
    </Fragment>
  );
};

export default Testimonials;
